import React, { useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.scss';
import { KeyLoanCharacteristicsSectionProps } from '../types';
import { TableRow } from './tableRow';
import { grandfatheredTypes, interestTypes, loanPurposeTypes } from './utils/statusCodes';
import CopyContentToClipBoard from './CopyContentToClipBoard';

export const KeyLoanCharacteristicsSection = ({
  isCollapsed,
  onToggle,
  keyLoanData,
  options
}: KeyLoanCharacteristicsSectionProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('Copied'),
      subHeader: String(' ')
    }
  });

  const loanExtrapolation = (value: string) => {
    if (!/^\d{4}$/.test(value)) return value;

    let y = value.slice(0, 2);
    const yNum = Number(y);
    let m = value.slice(2);
    const mNum = Number(m);

    if (yNum > 1) {
      y = `${y} ${t('PI-Portfolio-Management-Request-Overview-Years')}`;
    } else {
      y = `${y} ${t('PI-Portfolio-Management-Request-Overview-Year')}`;
    }
    if (mNum > 1) {
      m = `${m} ${t('PI-Portfolio-Management-Request-Overview-Months')}`;
    } else {
      m = `${m} ${t('PI-Portfolio-Management-Request-Overview-Month')}`;
    }

    return `${y} ${m}`;
  };

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>

      <Box className={`${styles.keyLoanContainer} ${isCollapsed ? styles.collapsed : ''}`}>
        <Box className={styles.accordionContainer} onClick={onToggle}>
          <Typography variant="h6" className={styles.accordionTitle}>
            {t('Key-Loan-Characteristics')}
          </Typography>
          <Button
            type="button"
            className={styles.toggleButton}
            id={styles.toggleButton}
            name="lender"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.SECONDARY}
            onClick={onToggle}
            leadingIcon={() => (
              <CmhcIcon
                className={styles.accordionIcon}
                size={CmhcIconSize.MEDIUM}
                type={isCollapsed ? 'icon_chevron_up' : 'icon_chevron_down'}
              />
            )}
            text={{
              value: ''
            }}
          />
        </Box>
        <Box className={styles.accordionBody}>
          <Box className={styles.tableContainer}>
            <Box>
              <TableRow
                leftTitle={t('Original-Lending-Value')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.originalLendingValue)}
                    clipboardContent={keyLoanData?.originalLendingValue || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.originalLendingValue)}
                      cellContent={<span>{keyLoanData?.originalLendingValue}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle=""
                rightValue={() => <></>}
                className={styles.hideCellTopBorder}
              />
              <TableRow
                leftTitle={t('Original-Loan-Amount-Approved-Limit')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.originalLoanAmount)}
                    clipboardContent={keyLoanData?.originalLoanAmount || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.originalLoanAmount)}
                      cellContent={<span>{keyLoanData?.originalLoanAmount}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Current-Loan-Amount')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.currentLoanAmount)}
                    clipboardContent={keyLoanData?.currentLoanAmount || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.currentLoanAmount)}
                      cellContent={<span>{keyLoanData?.currentLoanAmount}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Original-LTV-Ratio')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.originalLTVRatio)}
                    clipboardContent={keyLoanData?.originalLTVRatio || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.originalLTVRatio)}
                      cellContent={<span>{keyLoanData?.originalLTVRatio}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Current-LTV-Ratio')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.currentLTVRatio)}
                    clipboardContent={keyLoanData?.currentLTVRatio || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.currentLTVRatio)}
                      cellContent={<span>{keyLoanData?.currentLTVRatio}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
            </Box>

            <Box>
              <TableRow
                leftTitle={t('Loan-Term')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(loanExtrapolation(keyLoanData?.loanTerm || '') || '')}
                    clipboardContent={loanExtrapolation(keyLoanData?.loanTerm || '') || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.loanTerm)}
                      cellContent={<span>{loanExtrapolation(keyLoanData?.loanTerm || '')}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Interest-Rate')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      formatRatio(Number(keyLoanData?.interestRate || ''), i18n.language)
                    )}
                    clipboardContent={formatRatio(
                      Number(keyLoanData?.interestRate || ''),
                      i18n.language
                    )}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.interestRate)}
                      cellContent={
                        <span>
                          {formatRatio(Number(keyLoanData?.interestRate || ''), i18n.language)}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Interest-Type')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      interestTypes(keyLoanData?.interestType || '', t) || ''
                    )}
                    clipboardContent={interestTypes(keyLoanData?.interestType || '', t) || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.interestType)}
                      cellContent={<span>{interestTypes(keyLoanData?.interestType || '', t)}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Payment-Reset-Frequency')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.paymentResetFrequency)}
                    clipboardContent={keyLoanData?.paymentResetFrequency || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.paymentResetFrequency)}
                      cellContent={
                        <span>
                          {Number(keyLoanData?.paymentResetFrequency) > 1
                            ? `${keyLoanData?.paymentResetFrequency} ${t('PI-Portfolio-Management-Request-Overview-Years')}`
                            : `${keyLoanData?.paymentResetFrequency} ${t('PI-Portfolio-Management-Request-Overview-Year')}`}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Interest-Adjustment-Date')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.interestAdjustmentDate)}
                    clipboardContent={keyLoanData?.interestAdjustmentDate || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.interestAdjustmentDate)}
                      cellContent={<span>{keyLoanData?.interestAdjustmentDate}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Term-Expiry-Date')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(keyLoanData?.loanTermExpiryDate)}
                    clipboardContent={keyLoanData?.loanTermExpiryDate || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.loanTermExpiryDate)}
                      cellContent={<span>{keyLoanData?.loanTermExpiryDate}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
            </Box>

            <Box>
              <TableRow
                leftTitle={t('Grandfathered')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      grandfatheredTypes(keyLoanData?.grandfathered || '', t)
                    )}
                    clipboardContent={grandfatheredTypes(keyLoanData?.grandfathered || '', t)}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={
                        keyLoanData?.grandfathered === '0'
                          ? '1'
                          : String(keyLoanData?.grandfathered)
                      }
                      cellContent={
                        <Stack direction="row" gap={1}>
                          <span>{grandfatheredTypes(keyLoanData?.grandfathered || '', t)}</span>
                          {options && options.isToolTip && (
                            <Tooltip
                              title={options.toolTipDesc}
                              placement="top-start"
                              arrow
                              slotProps={{
                                tooltip: {
                                  className: styles.grandfatheredToolTip
                                },
                                arrow: {
                                  className: styles.tooltipArrow
                                }
                              }}
                            >
                              <div>
                                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                              </div>
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Loan-Purpose-Type')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      loanPurposeTypes(keyLoanData?.loanPurposeType || '', t)
                    )}
                    clipboardContent={loanPurposeTypes(keyLoanData?.loanPurposeType || '', t)}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.loanPurposeType)}
                      cellContent={
                        <span>{loanPurposeTypes(keyLoanData?.loanPurposeType || '', t)}</span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Qualifying-Total-Debt-Service')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      formatRatio(
                        Number(keyLoanData?.qualifyingTotalDebtService || ''),
                        i18n.language
                      )
                    )}
                    clipboardContent={formatRatio(
                      Number(keyLoanData?.qualifyingTotalDebtService || ''),
                      i18n.language
                    )}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.qualifyingTotalDebtService)}
                      cellContent={
                        <span>
                          {formatRatio(
                            Number(keyLoanData?.qualifyingTotalDebtService || ''),
                            i18n.language
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Qualifying-Gross-Debt-Service')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      formatRatio(
                        Number(keyLoanData?.qualifyingGrossDebtService || ''),
                        i18n.language
                      )
                    )}
                    clipboardContent={formatRatio(
                      Number(keyLoanData?.qualifyingGrossDebtService || ''),
                      i18n.language
                    )}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.qualifyingGrossDebtService)}
                      cellContent={
                        <span>
                          {formatRatio(
                            Number(keyLoanData?.qualifyingGrossDebtService || ''),
                            i18n.language
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Original-Amortization')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      loanExtrapolation(keyLoanData?.originalAmortization || '')
                    )}
                    clipboardContent={loanExtrapolation(keyLoanData?.originalAmortization || '')}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.originalAmortization)}
                      cellContent={
                        <span>{loanExtrapolation(keyLoanData?.originalAmortization || '')}</span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Remaining-Amortization')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      loanExtrapolation(keyLoanData?.remainingAmortization || '')
                    )}
                    clipboardContent={loanExtrapolation(keyLoanData?.remainingAmortization || '')}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(keyLoanData?.remainingAmortization)}
                      cellContent={
                        <span>{loanExtrapolation(keyLoanData?.remainingAmortization || '')}</span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
